import React, { useState, useEffect } from 'react';
import { Calendar, Clock, X, Plus, Trash2, Check, Sparkles, AlertCircle } from 'lucide-react';
import api from '../services/api';

const DAYS = [
    { id: 1, name: 'Lundi', short: 'Lun' },
    { id: 2, name: 'Mardi', short: 'Mar' },
    { id: 3, name: 'Mercredi', short: 'Mer' },
    { id: 4, name: 'Jeudi', short: 'Jeu' },
    { id: 5, name: 'Vendredi', short: 'Ven' },
    { id: 6, name: 'Samedi', short: 'Sam' },
    { id: 7, name: 'Dimanche', short: 'Dim' },
];

interface ScheduleSlot {
    id?: number;
    day_of_week: number;
    day_name: string;
    start_time: string;
    end_time: string;
    role_note: string;
}

interface CoachScheduleModalProps {
    isOpen: boolean;
    coach: any;
    onClose: () => void;
    onSuccess: () => void;
}

const CoachScheduleModal: React.FC<CoachScheduleModalProps> = ({ isOpen, coach, onClose, onSuccess }) => {
    const [schedules, setSchedules] = useState<ScheduleSlot[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

    // Initialisation / Chargement du planning du coach
    useEffect(() => {
        if (isOpen && coach?.id) {
            fetchCoachSchedules();
        } else {
            setSchedules([]);
            setFeedback(null);
        }
    }, [isOpen, coach]);

    const fetchCoachSchedules = async () => {
        try {
            setLoading(true);
            const res = await api.get(`/coaches/${coach.id}/schedules`);
            const loaded: ScheduleSlot[] = (res.data || []).map((s: any) => ({
                id: s.id,
                day_of_week: s.day_of_week,
                day_name: s.day_name,
                start_time: s.start_time.substring(0, 5),
                end_time: s.end_time.substring(0, 5),
                role_note: s.role_note || 'Présence en salle / Fitness'
            }));
            setSchedules(loaded);
        } catch (error) {
            console.error('Erreur chargement planning coach', error);
            setFeedback({ type: 'error', message: 'Impossible de charger le planning du coach' });
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen || !coach) return null;

    // Ajouter un créneau pour un jour
    const handleAddSlot = (dayId: number) => {
        const day = DAYS.find(d => d.id === dayId);
        if (!day) return;
        setSchedules(prev => [
            ...prev,
            {
                day_of_week: dayId,
                day_name: day.name,
                start_time: '08:00',
                end_time: '14:00',
                role_note: 'Présence en salle / Fitness'
            }
        ]);
    };

    // Supprimer un créneau
    const handleRemoveSlot = (index: number) => {
        setSchedules(prev => prev.filter((_, i) => i !== index));
    };

    // Mettre à jour un champ d'un créneau
    const handleUpdateSlot = (index: number, field: keyof ScheduleSlot, value: any) => {
        setSchedules(prev => prev.map((slot, i) => i === index ? { ...slot, [field]: value } : slot));
    };

    // Raccourci : Appliquer du Lundi au Vendredi (08h - 14h)
    const handleQuickPresetWeekdays = () => {
        const slots: ScheduleSlot[] = [1, 2, 3, 4, 5].map(dayId => ({
            day_of_week: dayId,
            day_name: DAYS.find(d => d.id === dayId)?.name || '',
            start_time: '08:00',
            end_time: '14:00',
            role_note: 'Présence en salle / Fitness'
        }));
        setSchedules(slots);
    };

    // Raccourci : Tout effacer (Repos complet)
    const handleClearAll = () => {
        if (window.confirm('Voulez-vous réinitialiser tous les jours de présence de ce coach ?')) {
            setSchedules([]);
        }
    };

    // Enregistrer le planning
    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setSaving(true);
            setFeedback(null);

            // Validation : start_time < end_time
            for (const s of schedules) {
                if (!s.start_time || !s.end_time) {
                    setFeedback({ type: 'error', message: `Veuillez spécifier l'heure de début et de fin pour ${s.day_name}` });
                    setSaving(false);
                    return;
                }
                if (s.start_time >= s.end_time) {
                    setFeedback({ type: 'error', message: `Pour ${s.day_name}, l'heure de début doit précéder l'heure de fin` });
                    setSaving(false);
                    return;
                }
            }

            await api.post(`/coaches/${coach.id}/schedules`, { schedules });
            setFeedback({ type: 'success', message: 'Planning du coach enregistré avec succès !' });
            setTimeout(() => {
                onSuccess();
                onClose();
            }, 800);
        } catch (error: any) {
            console.error('Erreur sauvegarde planning:', error);
            setFeedback({ type: 'error', message: error.response?.data?.error || 'Erreur lors de la sauvegarde du planning' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full border border-gray-100 overflow-hidden my-8 animate-in fade-in zoom-in-95 duration-150">
                
                {/* Header */}
                <div className="px-6 py-5 bg-gradient-to-r from-brand-dark via-gray-900 to-brand-dark text-white flex items-center justify-between border-b border-gray-800">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-brand-primary/20 border border-brand-primary/40 flex items-center justify-center text-brand-primary">
                            <Calendar size={22} />
                        </div>
                        <div>
                            <h2 className="text-xl font-black">
                                Programmer les jours de présence
                            </h2>
                            <p className="text-xs text-gray-300">
                                Coach : <strong className="text-brand-primary font-bold">{coach.first_name} {coach.last_name}</strong> ({coach.specialties || 'Fitness'})
                            </p>
                        </div>
                    </div>
                    <button 
                        onClick={onClose}
                        className="p-1.5 text-gray-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Subheader / Raccourcis */}
                <div className="px-6 py-3 bg-amber-50/60 border-b border-amber-100 flex flex-wrap items-center justify-between gap-2 text-xs">
                    <span className="text-amber-900 font-medium">
                        Définissez les jours et créneaux horaires où ce coach est présent dans la salle de sport.
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={handleQuickPresetWeekdays}
                            className="px-2.5 py-1 bg-white hover:bg-amber-100 text-amber-900 border border-amber-200 rounded-md font-bold transition-colors flex items-center gap-1 shadow-2xs"
                        >
                            <Sparkles size={13} className="text-amber-600" />
                            Lun - Ven (08h - 14h)
                        </button>
                        <button
                            type="button"
                            onClick={handleClearAll}
                            className="px-2.5 py-1 bg-white hover:bg-red-50 text-red-700 border border-red-200 rounded-md font-semibold transition-colors"
                        >
                            Effacer tout
                        </button>
                    </div>
                </div>

                {feedback && (
                    <div className={`mx-6 mt-4 p-3 rounded-xl border flex items-center gap-2 text-xs font-bold ${
                        feedback.type === 'success' 
                            ? 'bg-emerald-50 text-emerald-800 border-emerald-200' 
                            : 'bg-red-50 text-red-800 border-red-200'
                    }`}>
                        {feedback.type === 'success' ? <Check size={16} /> : <AlertCircle size={16} />}
                        <span>{feedback.message}</span>
                    </div>
                )}

                {/* Body : Jours de la semaine */}
                <form onSubmit={handleSave} className="p-6 space-y-4">
                    {loading ? (
                        <div className="py-12 text-center text-gray-500 text-sm">
                            Chargement du planning...
                        </div>
                    ) : (
                        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
                            {DAYS.map(day => {
                                const daySlots = schedules
                                    .map((s, idx) => ({ ...s, originalIndex: idx }))
                                    .filter(s => s.day_of_week === day.id);
                                const isAssigned = daySlots.length > 0;

                                return (
                                    <div 
                                        key={day.id} 
                                        className={`rounded-xl border transition-all ${
                                            isAssigned 
                                                ? 'bg-white border-amber-300/80 shadow-xs ring-1 ring-amber-100' 
                                                : 'bg-gray-50/70 border-gray-200'
                                        }`}
                                    >
                                        <div className="p-3.5 flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <span className={`w-8 h-8 rounded-lg flex items-center justify-center font-black text-xs ${
                                                    isAssigned 
                                                        ? 'bg-brand-primary text-brand-dark' 
                                                        : 'bg-gray-200 text-gray-600'
                                                }`}>
                                                    {day.short}
                                                </span>
                                                <div>
                                                    <h4 className="font-bold text-sm text-gray-800">
                                                        {day.name}
                                                    </h4>
                                                    <span className="text-[11px] text-gray-500">
                                                        {isAssigned 
                                                            ? `${daySlots.length} créneau(x) programmé(s)` 
                                                            : 'Non programmé (Repos)'}
                                                    </span>
                                                </div>
                                            </div>

                                            <button
                                                type="button"
                                                onClick={() => handleAddSlot(day.id)}
                                                className="px-2.5 py-1 text-xs font-bold text-brand-dark bg-yellow-400/30 hover:bg-yellow-400 border border-yellow-400/60 rounded-md transition-colors flex items-center gap-1 cursor-pointer"
                                            >
                                                <Plus size={14} />
                                                Ajouter créneau
                                            </button>
                                        </div>

                                        {/* Créneaux du jour */}
                                        {daySlots.length > 0 && (
                                            <div className="px-3.5 pb-3.5 pt-1 space-y-2 border-t border-amber-100/60">
                                                {daySlots.map(slot => (
                                                    <div 
                                                        key={slot.originalIndex}
                                                        className="flex flex-wrap items-center gap-2 p-2 bg-amber-50/40 rounded-lg border border-amber-200/50 text-xs"
                                                    >
                                                        <div className="flex items-center gap-1.5">
                                                            <Clock size={14} className="text-gray-500" />
                                                            <span className="font-bold text-gray-700">De :</span>
                                                            <input
                                                                type="time"
                                                                value={slot.start_time}
                                                                onChange={(e) => handleUpdateSlot(slot.originalIndex, 'start_time', e.target.value)}
                                                                className="px-2 py-1 bg-white border border-gray-300 rounded font-mono font-bold text-gray-800 focus:ring-1 focus:ring-brand-primary focus:border-brand-primary"
                                                                required
                                                            />
                                                        </div>

                                                        <div className="flex items-center gap-1.5">
                                                            <span className="font-bold text-gray-700">À :</span>
                                                            <input
                                                                type="time"
                                                                value={slot.end_time}
                                                                onChange={(e) => handleUpdateSlot(slot.originalIndex, 'end_time', e.target.value)}
                                                                className="px-2 py-1 bg-white border border-gray-300 rounded font-mono font-bold text-gray-800 focus:ring-1 focus:ring-brand-primary focus:border-brand-primary"
                                                                required
                                                            />
                                                        </div>

                                                        <div className="flex-1 min-w-[160px]">
                                                            <input
                                                                type="text"
                                                                placeholder="Activité / Mission (ex: Musculation, Coaching)"
                                                                value={slot.role_note}
                                                                onChange={(e) => handleUpdateSlot(slot.originalIndex, 'role_note', e.target.value)}
                                                                className="w-full px-2 py-1 bg-white border border-gray-300 rounded text-gray-800 placeholder-gray-400 focus:ring-1 focus:ring-brand-primary focus:border-brand-primary text-xs"
                                                            />
                                                        </div>

                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveSlot(slot.originalIndex)}
                                                            className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                                                            title="Supprimer ce créneau"
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Footer */}
                    <div className="pt-4 border-t border-gray-200 flex items-center justify-between">
                        <span className="text-xs text-gray-500">
                            Total : <strong>{schedules.length}</strong> créneau(x) configuré(s) pour la semaine
                        </span>
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 border border-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-50 text-xs transition-colors"
                            >
                                Annuler
                            </button>
                            <button
                                type="submit"
                                disabled={saving}
                                className="px-6 py-2 bg-brand-primary text-brand-dark font-black rounded-lg hover:bg-yellow-400 text-xs transition-colors shadow-sm disabled:opacity-50 flex items-center gap-1.5"
                            >
                                {saving ? 'Enregistrement...' : 'Enregistrer le planning'}
                            </button>
                        </div>
                    </div>
                </form>

            </div>
        </div>
    );
};

export default CoachScheduleModal;

